'use client';

import React, { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { AuthComponentProps } from '../types'
import { 
  TextInput, 
  PasswordInput, 
  Button, 
  Stack, 
  Paper, 
  Title,
  Text,
  Anchor,
  Center,
  Checkbox
} from '@mantine/core'
import { validateEmail, validatePassword, validateName, validatePhone } from '../utils/validation'
import { createUserProfile, createOrganization } from '../services/firestore'

interface RegisterProps extends AuthComponentProps {
  onLoginClick?: () => void
  title?: string
}

export const Register: React.FC<RegisterProps> = ({ 
  onSuccess, 
  onError,
  onLoginClick,
  title = "Create Account",
  logo
}) => {
  const { signUp } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [nameError, setNameError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [address, setAddress] = useState('')
  const [organizationName, setOrganizationName] = useState('')
  const [createOrg, setCreateOrg] = useState(false)

  const validateForm = () => {
    const nameValidation = validateName(name)
    const emailValidation = validateEmail(email)
    const passwordValidation = validatePassword(password)
    const phoneValidation = validatePhone(phone)

    setNameError(nameValidation.error)
    setEmailError(emailValidation.error)
    setPasswordError(passwordValidation.error)
    setPhoneError(phoneValidation.error)

    return nameValidation.isValid && 
           emailValidation.isValid && 
           passwordValidation.isValid &&
           phoneValidation.isValid
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    
    try {
      const result = await signUp(email, password, name)
      if (result.user) {
        const userData = {
          uid: result.user.uid,
          email,
          displayName: name,
          phoneNumber: phone,
          dateOfBirth,
          address,
          organizations: [],
          role: 'user'
        }
        
        await createUserProfile(result.user.uid, userData)
        
        if (createOrg && organizationName.trim()) {
          await createOrganization({
            name: organizationName.trim(),
            ownerId: result.user.uid,
            members: [{
              userId: result.user.uid,
              role: 'owner' as const,
              joinedAt: new Date()
            }]
          })
        }
      }
      onSuccess?.()
    } catch (error) {
      onError?.(error as Error)
    }
  }

  return (
    <Paper radius="md" p="xl" withBorder>
      {logo && (
        <Center mb="md">
          {logo}
        </Center>
      )}
      <Title order={2} mb="md" ta="center" c="blue">
        {title}
      </Title>
      <Text c="dimmed" size="sm" mb="lg" ta="center">
        Enter your details to create your account
      </Text>

      <form onSubmit={handleRegister}>
        <Stack>
          <TextInput
            required
            label="Name"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={nameError}
            radius="md"
          />

          <TextInput
            required
            label="Email"
            placeholder="hello@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailError}
            radius="md"
          />

          <PasswordInput
            required
            label="Password"
            placeholder="Your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={passwordError}
            radius="md"
          />

          <TextInput
            label="Phone Number"
            placeholder="+1 234 567 8900"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={phoneError}
            radius="md"
          />

          <TextInput
            label="Date of Birth"
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
          />

          <TextInput
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />

          <Checkbox
            label="Create an organization"
            checked={createOrg}
            onChange={(e) => setCreateOrg(e.currentTarget.checked)}
            mt="md"
          />

          {createOrg && (
            <TextInput
              label="Organization Name"
              placeholder="Enter organization name"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              mt="xs"
            />
          )}

          <Button type="submit" fullWidth radius="md" color="green">
            Create account
          </Button>
        </Stack>
      </form>

      <Text c="dimmed" size="sm" ta="center" mt="md">
        Already have an account?{' '}
        <Anchor component="button" type="button" onClick={onLoginClick}>
          Login
        </Anchor>
      </Text>
    </Paper>
  )
} 